/** Quadratic edge patch, shared by the native renderer and original editor.
 * Offsets are displacements of the four side MIDPOINTS, clockwise from top.
 * Zero offsets reproduce the exact bilinear quadrilateral; corners never move.
 */
export interface WarpPoint {
  x: number;
  y: number;
}
export const zeroEdges = (): WarpPoint[] =>
  Array.from({ length: 4 }, () => ({ x: 0, y: 0 }));
export const hasEdgeWarp = (edges?: readonly WarpPoint[]) =>
  !!edges?.some((p) => p.x !== 0 || p.y !== 0);
export function edgeOutline(
  q: readonly WarpPoint[],
  e: readonly WarpPoint[],
  steps = 24,
) {
  const points: WarpPoint[] = [];
  for (let side = 0; side < 4; side++) {
    const a = q[side]!,
      b = q[(side + 1) % 4]!,
      d = e[side]!;
    for (let i = 0; i < steps; i++) {
      const t = i / steps,
        w = 4 * t * (1 - t);
      points.push({
        x: a.x + (b.x - a.x) * t + d.x * w,
        y: a.y + (b.y - a.y) * t + d.y * w,
      });
    }
  }
  return points;
}
/** Exact extrema of the four quadratic boundary curves, also after an affine map. */
export function edgeExtrema(q: readonly WarpPoint[], e: readonly WarpPoint[]) {
  const result = [...q];
  for (let i = 0; i < 4; i++) {
    const a = q[i]!,
      b = q[(i + 1) % 4]!,
      d = e[i]!;
    for (const axis of ["x", "y"] as const) {
      if (!d[axis]) continue;
      const t = (b[axis] - a[axis] + 4 * d[axis]) / (8 * d[axis]);
      if (t > 0 && t < 1)
        result.push({
          x: a.x + (b.x - a.x) * t + 4 * d.x * t * (1 - t),
          y: a.y + (b.y - a.y) * t + 4 * d.y * t * (1 - t),
        });
    }
  }
  return result;
}
function coefficients(
  q: readonly WarpPoint[],
  e: readonly WarpPoint[],
  axis: "x" | "y",
) {
  const [a, b, c, d] = q.map((p) => p[axis]) as [
    number,
    number,
    number,
    number,
  ];
  const [top, right, bottom, left] = e.map((p) => p[axis]) as [
    number,
    number,
    number,
    number,
  ];
  return [
    a,
    b - a + 4 * top,
    d - a + 4 * left,
    a - b + c - d + 4 * (bottom - top + right - left),
    -4 * top,
    -4 * left,
    4 * (top - bottom),
    4 * (left - right),
  ] as const;
}
export function edgePatch(q: readonly WarpPoint[], e: readonly WarpPoint[]) {
  const X = coefficients(q, e, "x"),
    Y = coefficients(q, e, "y");
  const value = (c: readonly number[], u: number, v: number) =>
    c[0]! +
    u * (c[1]! + u * c[4]!) +
    v * (c[2]! + v * c[5]!) +
    u * v * (c[3]! + u * c[6]! + v * c[7]!);
  const du = (c: readonly number[], u: number, v: number) =>
    c[1]! + 2 * u * c[4]! + v * (c[3]! + 2 * u * c[6]! + v * c[7]!);
  const dv = (c: readonly number[], u: number, v: number) =>
    c[2]! + 2 * v * c[5]! + u * (c[3]! + u * c[6]! + 2 * v * c[7]!);
  const [a, b, , d] = q as [WarpPoint, WarpPoint, WarpPoint, WarpPoint];
  const ax = b.x - a.x,
    ay = b.y - a.y,
    bx = d.x - a.x,
    by = d.y - a.y,
    det = ax * by - ay * bx;
  const extent = Math.max(1e-6, Math.hypot(ax, ay), Math.hypot(bx, by));
  return {
    point: (u: number, v: number): WarpPoint => ({
      x: value(X, u, v),
      y: value(Y, u, v),
    }),
    jacobian: (u: number, v: number) =>
      du(X, u, v) * dv(Y, u, v) - dv(X, u, v) * du(Y, u, v),
    inverse(x: number, y: number, margin = 0): WarpPoint | null {
      let u = Math.max(
        -margin,
        Math.min(1 + margin, ((x - a.x) * by - (y - a.y) * bx) / det),
      );
      let v = Math.max(
        -margin,
        Math.min(1 + margin, (ax * (y - a.y) - ay * (x - a.x)) / det),
      );
      for (let i = 0; i < 16; i++) {
        const dx = value(X, u, v) - x,
          dy = value(Y, u, v) - y;
        if (Math.abs(dx) + Math.abs(dy) < extent * 1e-8) return { x: u, y: v };
        const xu = du(X, u, v),
          xv = dv(X, u, v),
          yu = du(Y, u, v),
          yv = dv(Y, u, v),
          j = xu * yv - xv * yu;
        if (Math.abs(j) < extent * extent * 1e-12) return null;
        const su = (dx * yv - dy * xv) / j,
          sv = (dy * xu - dx * yu) / j;
        const damping = Math.max(1, Math.abs(su) * 2, Math.abs(sv) * 2);
        u = Math.max(-margin, Math.min(1 + margin, u - su / damping));
        v = Math.max(-margin, Math.min(1 + margin, v - sv / damping));
      }
      return null;
    },
  };
}
// The Jacobian is bicubic. Bernstein bounds certify positivity over each box;
// subdivision rejects folds even when all four corners have positive Jacobians.
const bernstein = (f: readonly number[]) => [
  f[0]!,
  (18 * f[1]! - 9 * f[2]! - 5 * f[0]! + 2 * f[3]!) / 6,
  (-9 * f[1]! + 18 * f[2]! + 2 * f[0]! - 5 * f[3]!) / 6,
  f[3]!,
];
export function validEdgeWarp(
  q: readonly WarpPoint[],
  e: readonly WarpPoint[],
) {
  if (
    q.length !== 4 ||
    e.length !== 4 ||
    [...q, ...e].some(
      (p) => !p || !Number.isFinite(p.x) || !Number.isFinite(p.y),
    )
  )
    return false;
  const patch = edgePatch(q, e),
    scale = Math.max(
      ...q.map((p, i) =>
        Math.hypot(p.x - q[(i + 1) % 4]!.x, p.y - q[(i + 1) % 4]!.y),
      ),
    );
  if (!scale || e.some((p) => Math.hypot(p.x, p.y) > scale * 4)) return false;
  const eps = scale * scale * 1e-7;
  function positive(
    u: number,
    v: number,
    size: number,
    depth: number,
  ): boolean {
    const rows = Array.from({ length: 4 }, (_, j) =>
      bernstein(
        Array.from({ length: 4 }, (_, i) =>
          patch.jacobian(u + (size * i) / 3, v + (size * j) / 3),
        ),
      ),
    );
    const coefficients = Array.from({ length: 4 }, (_, i) =>
      bernstein(rows.map((r) => r[i]!)),
    ).flat();
    if (Math.min(...coefficients) > eps) return true;
    if (depth === 0 || Math.max(...coefficients) <= eps) return false;
    const h = size / 2;
    return (
      positive(u, v, h, depth - 1) &&
      positive(u + h, v, h, depth - 1) &&
      positive(u, v + h, h, depth - 1) &&
      positive(u + h, v + h, h, depth - 1)
    );
  }
  return positive(0, 0, 1, 5);
}
export function limitEdgeWarp(
  q: readonly WarpPoint[],
  from: readonly WarpPoint[],
  to: readonly WarpPoint[],
) {
  if (validEdgeWarp(q, to)) return to.map((p) => ({ ...p }));
  let low = 0,
    high = 1;
  const at = (t: number) =>
    from.map((p, i) => ({
      x: p.x + (to[i]!.x - p.x) * t,
      y: p.y + (to[i]!.y - p.y) * t,
    }));
  for (let i = 0; i < 16; i++) {
    const mid = (low + high) / 2;
    if (validEdgeWarp(q, at(mid))) low = mid;
    else high = mid;
  }
  return at(low);
}
interface Pixels {
  width: number;
  height: number;
  data: Uint8ClampedArray;
}
/** Inverse resampling, premultiplied interpolation: no triangle seams or dark alpha fringes. */
export function paintEdgeWarp(
  source: Pixels,
  target: Pixels,
  q: readonly WarpPoint[],
  e: readonly WarpPoint[],
  map?: (uv: WarpPoint) => WarpPoint,
  margin = 0,
) {
  const patch = edgePatch(q, e),
    sw = source.width,
    sh = source.height,
    src = source.data,
    dst = target.data;
  for (let y = 0; y < target.height; y++)
    for (let x = 0; x < target.width; x++) {
      const local = patch.inverse(x + 0.5, y + 0.5, margin);
      if (!local) continue;
      const uv = map ? map(local) : local,
        sx = uv.x * sw - 0.5,
        sy = uv.y * sh - 0.5;
      if (
        !Number.isFinite(sx) ||
        !Number.isFinite(sy) ||
        sx < -0.5 ||
        sy < -0.5 ||
        sx > sw - 0.5 ||
        sy > sh - 0.5
      )
        continue;
      const ix = Math.floor(sx),
        iy = Math.floor(sy),
        fx = sx - ix,
        fy = sy - iy;
      let alpha = 0,
        r = 0,
        g = 0,
        b = 0;
      for (let j = 0; j < 2; j++)
        for (let i = 0; i < 2; i++) {
          const xx = Math.max(0, Math.min(sw - 1, ix + i)),
            yy = Math.max(0, Math.min(sh - 1, iy + j)),
            k = (yy * sw + xx) * 4,
            w = (i ? fx : 1 - fx) * (j ? fy : 1 - fy),
            a = src[k + 3]! * w;
          alpha += a;
          r += src[k]! * a;
          g += src[k + 1]! * a;
          b += src[k + 2]! * a;
        }
      if (alpha) {
        const k = (y * target.width + x) * 4;
        dst[k] = r / alpha;
        dst[k + 1] = g / alpha;
        dst[k + 2] = b / alpha;
        dst[k + 3] = alpha;
      }
    }
}
